import { ChangeDetectionStrategy, Component, input, output } from '@angular/core';
import { IdentidadSinteticaLocal } from '../models';
import { UiCardKpiComponent } from '@/app/shared/ui/components/ui-card-kpi.component';
import { UiTabsSegmentsComponent } from '@/app/shared/ui/components/ui-tabs-segments.component';

@Component({
  selector: 'app-selector-identidad-local',
  standalone: true,
  imports: [UiCardKpiComponent, UiTabsSegmentsComponent],
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: {
    'data-testid': 'selector-identidad-local',
  },
  template: `
    <app-ui-card-kpi
      class="selector-card"
      aria-label="Selector de identidad local"
      variant="compacta"
      subtitle="Usa estas identidades solo mientras pruebas la consola en tu equipo."
    >
      <p class="selector-card__status" data-testid="selector-identidad-estado">{{ selectedIdentityLabel() }} · {{ mfaVerificado() ? 'MFA verificado' : 'Sin MFA' }}</p>

      <div class="selector-card__grid">
        <div class="selector-card__field" role="group" aria-label="Identidad">
          <span class="selector-card__label">Identidad</span>
          <div class="selector-card__lista">
            @for (opcion of identityOptions(); track opcion.id) {
              <button
                type="button"
                class="selector-card__opcion"
                [class.selector-card__opcion--activa]="opcion.id === selectedIdentityId()"
                [attr.aria-pressed]="opcion.id === selectedIdentityId()"
                (click)="onIdentityChange(opcion.id)"
              >
                <span class="selector-card__marca" aria-hidden="true"></span>
                {{ opcion.label }}
              </button>
            }
          </div>
        </div>

        <div class="selector-card__mfa">
          <div>
            <p class="selector-card__label">MFA</p>
            <p class="selector-card__help">Simula si la identidad ya verificó MFA.</p>
          </div>

          <app-ui-tabs-segments
            mode="segments"
            [options]="mfaOptions"
            [selectedId]="mfaVerificado() ? 'verificado' : 'pendiente'"
            (changed)="onMfaSegmentChange($event)"
          />
        </div>
      </div>
    </app-ui-card-kpi>
  `,
  styles: [
    `
      .selector-card,
      .selector-card__grid {
        display: grid;
        gap: var(--esp-interno);
      }

      .selector-card__status {
        margin: 0;
        color: var(--muted);
        font-size: var(--t-caption);
      }

      .selector-card__field,
      .selector-card__mfa {
        display: grid;
        gap: var(--esp-interno);
      }

      .selector-card__lista {
        display: grid;
        gap: calc(var(--esp-interno) / 2);
      }

      .selector-card__opcion {
        display: flex;
        align-items: center;
        gap: var(--esp-interno);
        width: 100%;
        padding: calc(var(--esp-interno) * 0.7) var(--esp-interno);
        border: 1px solid var(--border);
        border-radius: var(--radius);
        background: var(--surface);
        color: var(--text);
        font: inherit;
        text-align: left;
        cursor: pointer;
        transition: background-color 120ms ease, border-color 120ms ease;
      }

      .selector-card__opcion:hover {
        background: var(--primary-subtle);
      }

      .selector-card__opcion:focus-visible {
        outline: 2px solid var(--primary);
        outline-offset: 2px;
      }

      .selector-card__opcion--activa {
        border-color: var(--primary);
        background: var(--primary-subtle);
        color: var(--primary);
        font-weight: 600;
      }

      .selector-card__marca {
        flex: none;
        width: 1.1rem;
        height: 1.1rem;
        border-radius: 50%;
        border: 2px solid var(--border);
        background: var(--surface);
        display: grid;
        place-items: center;
      }

      .selector-card__opcion--activa .selector-card__marca {
        border-color: var(--primary);
      }

      .selector-card__opcion--activa .selector-card__marca::after {
        content: '';
        width: 0.5rem;
        height: 0.5rem;
        border-radius: 50%;
        background: var(--primary);
      }

      .selector-card__mfa {
        align-items: center;
        grid-template-columns: minmax(0, 1fr) auto;
        padding-top: var(--esp-interno);
        border-top: 1px solid var(--border);
      }

      .selector-card__label {
        margin: 0;
        font-weight: 600;
        color: var(--text);
      }

      .selector-card__help {
        margin: calc(var(--esp-interno) / 4) 0 0;
        color: var(--muted);
      }

      @media (max-width: 768px) {
        .selector-card__mfa {
          grid-template-columns: 1fr;
        }
      }
    `,
  ],
})
export class SelectorIdentidadLocalComponent {
  protected readonly mfaOptions = [
    { id: 'pendiente', label: 'Sin MFA' },
    { id: 'verificado', label: 'MFA verificado' },
  ];
  readonly identidades = input.required<IdentidadSinteticaLocal[]>();
  readonly selectedIdentityId = input.required<IdentidadSinteticaLocal['corporateIdentityId'] | null>();
  readonly mfaVerificado = input.required<boolean>();
  readonly selectionChange = output<{
    corporateIdentityId: IdentidadSinteticaLocal['corporateIdentityId'];
    mfaVerificado: boolean;
  }>();

  protected identityOptions() {
    return this.identidades().map((identity) => ({
      id: identity.corporateIdentityId,
      label: identity.label,
    }));
  }

  protected onIdentityChange(corporateIdentityId: string): void {
    this.selectionChange.emit({
      corporateIdentityId: corporateIdentityId as IdentidadSinteticaLocal['corporateIdentityId'],
      mfaVerificado: this.mfaVerificado(),
    });
  }

  protected onMfaChange(mfaVerificado: boolean): void {
    if (!this.selectedIdentityId()) {
      return;
    }

    this.selectionChange.emit({
      corporateIdentityId: this.selectedIdentityId()!,
      mfaVerificado,
    });
  }

  protected onMfaSegmentChange(value: string): void {
    this.onMfaChange(value === 'verificado');
  }

  protected selectedIdentityLabel(): string {
    return this.identidades().find((identity) => identity.corporateIdentityId === this.selectedIdentityId())?.label ?? 'Sin identidad seleccionada';
  }
}
